import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Trash2 } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import type { NevoraDataProvider } from "../datos/dataProvider";
import {
  copiarDia,
  horarioValido,
  leerHorario,
  type Franja,
} from "../dominio/huecos";

const DIAS = [1, 2, 3, 4, 5, 6, 7] as const;
const LABORABLES = [1, 2, 3, 4, 5];
const nombreDia = (d: number) =>
  new Date(2026, 10, 1 + d) // 1/11/2026 fue domingo: 2/11 lunes…
    .toLocaleDateString("es-ES", { weekday: "long" });

/**
 * «Copiar a…» (Robert, 27/10/2026): las franjas de un día a los que se marquen, de una vez. Por defecto, el resto
 * de lunes a viernes. Se aplica en pantalla; vale al pulsar «Guardar mi horario».
 */
const CopiarDia = ({
  dia,
  t,
  onCopiar,
}: {
  dia: number;
  t: (k: string, o?: Record<string, unknown>) => string;
  onCopiar: (destinos: number[]) => void;
}) => {
  const [abierto, setAbierto] = useState(false);
  const [destinos, setDestinos] = useState<number[]>([]);
  const otros = DIAS.filter((d) => d !== dia);
  const abrir = (a: boolean) => {
    if (a) setDestinos(LABORABLES.filter((d) => d !== dia));
    setAbierto(a);
  };
  const alternar = (d: number, si: boolean) =>
    setDestinos((ds) => (si ? [...ds, d] : ds.filter((x) => x !== d)));
  return (
    <Popover open={abierto} onOpenChange={abrir}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8"
          aria-label={t("copiar_dia", { dia: nombreDia(dia) })}
        >
          <Copy className="size-4 mr-1" />
          {t("copiar_a")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3 space-y-2">
        <p className="text-sm">{t("copiar_titulo", { dia: nombreDia(dia) })}</p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7"
            onClick={() => setDestinos(LABORABLES.filter((d) => d !== dia))}
          >
            {t("copiar_laborables")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7"
            onClick={() => setDestinos([...otros])}
          >
            {t("copiar_todos")}
          </Button>
        </div>
        <fieldset className="space-y-1">
          <legend className="sr-only">
            {t("copiar_titulo", { dia: nombreDia(dia) })}
          </legend>
          {otros.map((d) => (
            <div key={d} className="flex items-center gap-2">
              <Checkbox
                id={`copiar-${dia}-${d}`}
                checked={destinos.includes(d)}
                onCheckedChange={(c) => alternar(d, c === true)}
              />
              <Label
                htmlFor={`copiar-${dia}-${d}`}
                className="font-normal cursor-pointer first-letter:uppercase"
              >
                {nombreDia(d)}
              </Label>
            </div>
          ))}
        </fieldset>
        <Button
          type="button"
          size="sm"
          className="w-full"
          disabled={!destinos.length}
          onClick={() => {
            onCopiar(destinos);
            setAbierto(false);
          }}
        >
          {t("copiar_aplicar")}
        </Button>
      </PopoverContent>
    </Popover>
  );
};

/**
 * 41 (Robert, 23-24/10/2026): «Mi horario de consultorías», en el Perfil de Jan, Robert y los
 * consultores. Franjas por día de la semana y el correo de su calendario de Google (lo comparte con
 * info@ como libre/ocupado para que lo que tenga ahí no se ofrezca). Los «siguiente paso» no cuentan.
 */
export const MiHorario = ({ saleId }: { saleId?: Identifier }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { identity } = useGetIdentity();
  const id = saleId ?? identity?.id;
  const clave = ["nevora", "agenda-de", String(id)];
  const { data, isPending } = useQuery({
    queryKey: clave,
    queryFn: () => dataProvider.agendaDe(id as Identifier),
    enabled: id != null,
  });
  const [franjas, setFranjas] = useState<Franja[]>([]);
  const [calendario, setCalendario] = useState("");
  const [guardando, setGuardando] = useState(false);
  useEffect(() => {
    setFranjas(leerHorario(data?.horario));
    setCalendario(data?.calendario_google ?? "");
  }, [data]);

  const t = (k: string, o?: Record<string, unknown>) =>
    translate(`nevora.agenda_propia.${k}`, o);

  const cambiar = (i: number, campo: "desde" | "hasta", v: string) =>
    setFranjas((fs) => fs.map((f, j) => (j === i ? { ...f, [campo]: v } : f)));
  const anadir = (dia: number) =>
    setFranjas((fs) => [...fs, { dia, desde: "10:00", hasta: "14:00" }]);
  const quitar = (i: number) =>
    setFranjas((fs) => fs.filter((_, j) => j !== i));
  const copiar = (dia: number, destinos: number[]) => {
    setFranjas((fs) => copiarDia(fs, dia, destinos));
    notify(t("copiado"), { type: "info" });
  };

  const guardar = async () => {
    if (!horarioValido(franjas)) {
      notify(t("error_horario"), { type: "warning" });
      return;
    }
    setGuardando(true);
    try {
      await dataProvider.guardarAgenda(
        id as Identifier,
        franjas,
        calendario.trim() || null,
      );
      await queryClient.invalidateQueries({ queryKey: clave });
      notify(t("guardado"), { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setGuardando(false);
    }
  };

  if (isPending) return null;
  return (
    <section
      className="rounded-lg border bg-card px-5 py-4 space-y-3"
      data-testid="mi-horario"
    >
      <h2 className="titulo-seccion">{t("mi_horario")}</h2>
      <p className="text-xs text-muted-foreground">{t("mi_horario_ayuda")}</p>
      <ul className="space-y-2">
        {DIAS.map((d) => (
          <li key={d} className="flex flex-wrap items-center gap-2">
            <span className="w-24 text-sm first-letter:uppercase">
              {nombreDia(d)}
            </span>
            {franjas.map((f, i) =>
              f.dia === d ? (
                <span key={i} className="flex items-center gap-1">
                  <Input
                    type="time"
                    aria-label={t("desde_dia", { dia: nombreDia(d) })}
                    className="h-8 w-28"
                    value={f.desde}
                    onChange={(e) => cambiar(i, "desde", e.target.value)}
                  />
                  <span className="text-muted-foreground">–</span>
                  <Input
                    type="time"
                    aria-label={t("hasta_dia", { dia: nombreDia(d) })}
                    className="h-8 w-28"
                    value={f.hasta}
                    onChange={(e) => cambiar(i, "hasta", e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    aria-label={t("quitar_franja")}
                    onClick={() => quitar(i)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </span>
              ) : null,
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8"
              onClick={() => anadir(d)}
            >
              <Plus className="size-4 mr-1" />
              {t("anadir_franja")}
            </Button>
            {franjas.some((f) => f.dia === d) ? (
              <CopiarDia
                dia={d}
                t={t}
                onCopiar={(destinos) => copiar(d, destinos)}
              />
            ) : null}
          </li>
        ))}
      </ul>
      <div className="space-y-1">
        <Label htmlFor="mi-calendario">{t("mi_calendario")}</Label>
        <Input
          id="mi-calendario"
          type="email"
          placeholder={String(identity?.email ?? "")}
          value={calendario}
          onChange={(e) => setCalendario(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          {t("mi_calendario_ayuda")}
        </p>
      </div>
      <Button type="button" disabled={guardando} onClick={guardar}>
        {t("guardar_horario")}
      </Button>
    </section>
  );
};
